import React, { useContext } from 'react';
import { InlineMath } from 'react-katex';
import { ThemeContext } from '../../context/ThemeContext';

function StatisticsBarChartDiagram({
  categories,
  counts,
  xLabel,
  yLabel,
  minHeight = 260,
  barColor = '#ff9900',
  yMax: yMaxOverride = null,
  maxBarHeight = 180,
  yLabelStep = 2,
  showCounts = false,
}) {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === 'dark' || theme === 'night';

  const axisColor = isDark ? '#ffffff' : '#000000';
  const gridColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)';

  const rawMax = Math.max(...counts);
  // zaokrąglamy w górę do parzystej liczby + mały zapas,
  // żeby skala wyglądała podobnie do 0–18 z matury
  const yMaxBase = Math.ceil(rawMax / 2) * 2;
  const yMax = yMaxOverride == null ? Math.max(4, yMaxBase + 2) : yMaxOverride;

  // linie siatki co 1 jednostkę
  const gridValues = [];
  for (let v = 1; v <= yMax; v += 1) {
    gridValues.push(v);
  }

  // Domyślnie etykiety są co 2 jednostki; bardziej zwarte diagramy mogą
  // pokazać każdą wartość, np. dokładną skalę 0–7 z arkusza CKE.
  const labelValues = [];
  for (let v = 0; v <= yMax; v += yLabelStep) {
    labelValues.push(v);
  }

  return (
    <div
      style={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        {/* kolumna osi Y */}
        <div
          style={{
            position: 'relative',
            width: 50,
            height: maxBarHeight,
            marginRight: 6,
          }}
        >
          {/* podpis osi Y – pionowo, bliżej liczb */}
          <div
            style={{
              position: 'absolute',
              left: -30, // bliżej niż wcześniej (np. -70)
              top: '50%',
              transform: 'translateY(-50%) rotate(-90deg)',
              fontSize: 12,
              color: axisColor,
              whiteSpace: 'nowrap',
            }}
          >
            {yLabel}
          </div>

          {/* liczby osi Y – pozycjonowane jak siatka, ale optycznie niżej */}
          {labelValues.map((v) => {
            const ratio = v / yMax;
            return (
              <div
                key={v}
                style={{
                  position: 'absolute',
                  bottom: `${ratio * 100}%`,
                  right: 0,
                  transform: 'translateY(85%)', // było 50% → niżej, mniej myląco
                  fontSize: 11,
                  color: axisColor,
                }}
              >
                {<InlineMath math={String(v)}/> }
              </div>
            );
          })}
        </div>

        {/* właściwy diagram: oś pionowa, gęsta siatka i słupki */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: `1px solid ${axisColor}`,
            borderBottom: `1px solid ${axisColor}`,
            padding: '8px 12px 4px 12px',
            gap: 6,
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              height: maxBarHeight,
            }}
          >
            {/* poziome linie siatki dla każdego „oczka” */}
            {gridValues.map((v) => {
              const ratio = v / yMax;
              return (
                <div
                  key={v}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: `${ratio * 100}%`,
                    borderBottom: `1px solid ${gridColor}`,
                  }}
                />
              );
            })}

            {/* słupki */}
            {categories.map((cat, idx) => {
              const value = counts[idx];
              const h = yMax > 0 ? (value / yMax) * maxBarHeight : 0;
              return (
                <div
                  key={idx}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flex: 1,
                  }}
                >
                  {showCounts && <div style={{position:'absolute',bottom:h+3,fontSize:11}}><InlineMath math={String(value)}/></div>}
                  <div
                    style={{
                      width: 26,
                      height: h,
                      backgroundColor: barColor,
                      border: `1px solid ${axisColor}`,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* etykiety kategorii na osi X */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 6,
              fontSize: 12,
              color: axisColor,
            }}
          >
            {categories.map((cat,idx) => (
              <div
                key={idx}
                style={{
                  textAlign: 'center',
                  flex: 1,
                }}
              >
                {typeof cat === 'number' ? <InlineMath math={String(cat).replace('.', '{,}')} /> : cat}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* opis osi poziomej */}
      <div
        style={{
          marginTop: 4,
          fontSize: 12,
          color: axisColor,
          textAlign: 'center',
        }}
      >
        {xLabel}
      </div>
    </div>
  );
}

export default StatisticsBarChartDiagram;
